import { Action, createAction, handleActions } from 'redux-actions'
import { call, put, select, takeLatest } from 'redux-saga/effects'

import {
  addFavoritePayment,
  fetchBillBeta,
  fetchBillPaymentMainBeta,
  removeFavoritePayment,
  removeRecentPayment,
} from '@/api/BillPaymentApi'
import * as Bridge from '@/api/BridgeApi'
import { getCSRF } from '@/api/CsrfApi'
import { deeplinkFn } from '@/components/App'
import { LEAVE_ALERT_TYPE } from '@/components/common/layer/Leave/LeaveLayer'
import { BILL_PAYMENT_ROUTES, IMap, STATUS_CODE } from '@/domains/CommonDomain'
import {
  BILLS_IDENTIFIER_TYPE_CODE,
  EmptyPaymentType,
  EmptyProductGroupType,
  IBillPaymentAddFavoriteRequest,
  IBillPaymentInquireRequest,
  IBillPaymentInquireResponse,
  IBillPaymentMainResponse,
  IBillPaymentRecentAndFavorite,
  IBillPaymentRemoveFavoriteRequest,
  IPaymentType,
  IProductGroupType,
  IProductType,
} from '@/domains/version/V2/BillPaymentDomain'
import { IRootState } from '@/modules'
import { errorActions } from '@/modules/common/error'
import { layerActions } from '@/modules/common/layer'
import { loadingActions } from '@/modules/common/loading'
import {
  billPaymentAmountActions,
  validateBillAsync,
} from '@/modules/version/V2/bill-payment/BillPaymentAmountModule'
import { createFetchAction, createPostAction } from '@/utils/actionUtils'
import {
  addRecentToBookMarks,
  Category,
  IFoldSetting,
  moveBookmarkToRecents,
  removeFromBookmarkAndRecents,
  storeFoldSetting,
} from '@/utils/favoriteUtils'
import { getFormattedCardNo, removeAllHypens } from '@/utils/formatUtils'
import { push } from '@/utils/historyUtils'
import { $ } from '@/utils/i18nUtils'
import { apiFailureReducer } from '@/utils/reducerUtils'
import { BILL_PAYMENT_FOLD_LOCAL_STORAGE_KEY, getItemFromLocalStorage } from '@/utils/storageUtils'

export const BILL_PAYMENT_MAIN = 'BillPaymentMainV2'
export const BILL_PAYMENT_GET_CODE = `${BILL_PAYMENT_MAIN}/getCode`

const BILL_PAYMENT_FAVORITE_ADD = `${BILL_PAYMENT_MAIN}/favorite/add`
const BILL_PAYMENT_FAVORITE_REMOVE = `${BILL_PAYMENT_MAIN}/favorite/remove`
const SET_PAY_STATE_BY_RECENTLY_AND_FAVORITE_MENU = `${BILL_PAYMENT_MAIN}/setPayStateByRecentlyAndFavoriteMenu`
const RESET_PAY_STATE_BY_RECENTLY_AND_FAVORITE_MENU = `${BILL_PAYMENT_MAIN}/resetPayStateByRecentlyAndFavoriteMenu`
const REMOVE_FAVORITE_AND_RECENTLY_ITEM = `${BILL_PAYMENT_MAIN}/removeFavoriteAndRecentlyItem`
const TOGGLE_FOLD_FAVORITE = `${BILL_PAYMENT_MAIN}/toggleFoldFavorite`
const TOGGLE_FOLD_RECENTLY = `${BILL_PAYMENT_MAIN}/toggleFoldRecently`
const SET_VIEW = `${BILL_PAYMENT_MAIN}/setView`

const SET_STARTTYPE = `${BILL_PAYMENT_MAIN}/setStartType`
const CLEAR_STARTTYPE = `${BILL_PAYMENT_MAIN}/clearStartType`

const SET_VERSION_INFO = `${BILL_PAYMENT_MAIN}/setVersionInfo`

export interface IFlattenProductType {
  paymentTypeCode: string
  paymentTypeName: string
  productGroupTypeCode: string
  productGroupTypeName: string
  productGroupTypeInitial: string
  productGroupTypeColor: string
  productTypeIdentifierName: string
  productTypeIdentifierType: string
  productTypeRedoYn: string
  productTypeName: string
  productTypeSwitchId: string
  productTypeTransactionFee: string
  productTypeIsAutoFilled: boolean
  productTypeVoucherList: string[]
  productappsBillType: string
  productQcPayable: boolean
  productTypeCode: string
}

export const EmptyFlattenProduct: IFlattenProductType = {
  paymentTypeCode: '',
  paymentTypeName: '',
  productGroupTypeCode: '',
  productGroupTypeName: '',
  productTypeCode: '',
  productGroupTypeInitial: '',
  productGroupTypeColor: '',
  productTypeIdentifierName: '',
  productTypeIdentifierType: '',
  productTypeRedoYn: '',
  productTypeName: '',
  productTypeSwitchId: '',
  productTypeTransactionFee: '',
  productTypeIsAutoFilled: false,
  productTypeVoucherList: [],
  productappsBillType: '',
  productQcPayable: true,
}

export type IFlattenProductTypeMap = IMap<IFlattenProductType>

export interface IBillPaymentMainView {
  invalidCustomerId: string
  invalidProductTypeName: string
}

export interface IBillPaymentMainState extends IBillPaymentMainResponse {
  flattenProductTypeMap: IFlattenProductTypeMap
  favoriteFold: boolean
  recentlyFold: boolean
  view: IBillPaymentMainView
  version: string
  mainStarttype: string
  restQuota: string
  totQuota: string
}

export const initialState: IBillPaymentMainState = {
  favoriteList: [],
  paymentTypeList: [],
  recentlyList: [],
  flattenProductTypeMap: {},
  favoriteFold: false,
  recentlyFold: false,
  view: {
    invalidCustomerId: '',
    invalidProductTypeName: '',
  },
  version: '',
  mainStarttype: '',
  restQuota: '',
  totQuota: '',
  minDisbursalAmount: '0.0',
}

export const billMainActions = {
  setPayStateByRecentlyAndFavoriteMenu: createAction<IBillPaymentRecentAndFavorite>(
    SET_PAY_STATE_BY_RECENTLY_AND_FAVORITE_MENU,
  ),
  removeFavoriteAndRecentlyItem: createAction<IBillPaymentRecentAndFavorite>(
    REMOVE_FAVORITE_AND_RECENTLY_ITEM,
  ),
  resetPayStateByRecentlyAndFavoriteMenu: createAction(
    RESET_PAY_STATE_BY_RECENTLY_AND_FAVORITE_MENU,
  ),
  toggleFoldFavorite: createAction(TOGGLE_FOLD_FAVORITE),
  toggleFoldRecently: createAction(TOGGLE_FOLD_RECENTLY),
  setView: createAction<Partial<IBillPaymentMainView>>(SET_VIEW),
  setVersionInfo: createAction(SET_VERSION_INFO),

  setStarttype: createAction<string>(SET_STARTTYPE),
  clearStarttype: createAction(CLEAR_STARTTYPE),
}

export const getCodeAsync = createFetchAction(BILL_PAYMENT_GET_CODE)
export const billMainAsync = createFetchAction(BILL_PAYMENT_MAIN)
export const billMainAddFavoriteAsync = createPostAction(BILL_PAYMENT_FAVORITE_ADD)
export const billMainRemoveFavoriteAsync = createPostAction(BILL_PAYMENT_FAVORITE_REMOVE)

// 최근 이용 => 즐겨찾기
export const addToFavorite = (
  state: IBillPaymentMainState,
  payload: IBillPaymentAddFavoriteRequest,
): IBillPaymentMainState => {
  const result = addRecentToBookMarks(
    {
      recents: state.recentlyList,
      bookmarks: state.favoriteList,
    },
    item => item.trxPayKey === payload.trxPayKey,
    item => ({ ...item, favorite: true }),
  )

  return {
    ...state,
    recentlyList: result.recents,
    favoriteList: result.bookmarks,
  }
}

// 즐겨찾기 => 최근 이용
export const removeFromFavorite = (
  state: IBillPaymentMainState,
  payload: IBillPaymentRemoveFavoriteRequest,
): IBillPaymentMainState => {
  const result = moveBookmarkToRecents(
    {
      recents: state.recentlyList,
      bookmarks: state.favoriteList,
    },
    item => item.trxPayKey === payload.trxPayKey,
    item => ({ ...item, favorite: false }),
  )

  return {
    ...state,
    recentlyList: result.recents,
    favoriteList: result.bookmarks,
  }
}

// 특정 아이템 삭제
export const removeFromFavoriteAndRecently = (
  state: IBillPaymentMainState,
  payload: IBillPaymentRemoveFavoriteRequest,
): IBillPaymentMainState => {
  const result = removeFromBookmarkAndRecents(
    {
      recents: state.recentlyList,
      bookmarks: state.favoriteList,
    },
    item => item.trxPayKey !== payload.trxPayKey,
  )

  return {
    ...state,
    recentlyList: result.recents,
    favoriteList: result.bookmarks,
  }
}

const normalizedProductType = (paymentTypes: IPaymentType[]): IFlattenProductTypeMap =>
  paymentTypes.reduce((acc, paymentType) => {
    const result: IFlattenProductTypeMap = {}
    paymentTypes.forEach(paymentType => {
      paymentType.productGroupList.forEach(productGroup => {
        productGroup.productTypeList.forEach(productType => {
          result[productType.code] = {
            paymentTypeCode: paymentType.code,
            paymentTypeName: paymentType.name,
            productGroupTypeCode: productGroup.code,
            productGroupTypeName: productGroup.name,
            productGroupTypeInitial: productGroup.initial,
            productGroupTypeColor: productGroup.color,
            productTypeCode: productType.code,
            productTypeIdentifierName: productType.identifierName,
            productTypeIdentifierType: productType.identifierType,
            productTypeRedoYn: productType.redoYn,
            productTypeName: productType.name,
            productTypeIsAutoFilled: productType.isAutoFilled,
            productTypeTransactionFee: productType.transactionFee,
            productTypeSwitchId: productType.switchId,
            productTypeVoucherList: [...productType.voucherList],
            productappsBillType: productType.appsBillType,
            productQcPayable: productType.qcPayable,
          }
        })
      })
    })

    return {
      ...acc,
      ...result,
    }
  }, {})

const reducer = {
  [getCodeAsync.SUCCESS]: (state: IBillPaymentMainState, action: Action<any>) => {
    const { paymentTypeList } = action.payload

    return {
      ...state,
      paymentTypeList,
      flattenProductTypeMap: normalizedProductType(paymentTypeList),
    }
  },
  [getCodeAsync.FAILURE]: apiFailureReducer,
  [billMainAsync.SUCCESS]: (
    state: IBillPaymentMainState,
    action: Action<any>,
  ): IBillPaymentMainState => {
    const {
      paymentTypeList,
      favoriteList,
      recentlyList,
      restQuota,
      totQuota,
      minDisbursalAmount,
    } = action.payload
    const foldSetting = getItemFromLocalStorage<IFoldSetting>(BILL_PAYMENT_FOLD_LOCAL_STORAGE_KEY)

    return {
      ...state,
      favoriteList,
      recentlyList,
      paymentTypeList,
      restQuota,
      totQuota,
      minDisbursalAmount,
      favoriteFold: (foldSetting && foldSetting[Category.FAVORITE]) ?? false,
      recentlyFold: (foldSetting && foldSetting[Category.RECENTLY]) ?? false,
      flattenProductTypeMap: normalizedProductType(paymentTypeList),
    }
  },
  [billMainAsync.FAILURE]: apiFailureReducer,
  [billMainAddFavoriteAsync.SUCCESS]: (
    state: IBillPaymentMainState,
    action: Action<IBillPaymentAddFavoriteRequest>,
  ) => addToFavorite(state, action.payload),
  [billMainAddFavoriteAsync.FAILURE]: (
    state: IBillPaymentMainState,
    action: Action<IBillPaymentAddFavoriteRequest>,
  ) => removeFromFavorite(state, action.payload),
  [billMainRemoveFavoriteAsync.SUCCESS]: (
    state: IBillPaymentMainState,
    action: Action<IBillPaymentRemoveFavoriteRequest>,
  ) => removeFromFavorite(state, action.payload),
  [billMainRemoveFavoriteAsync.FAILURE]: (
    state: IBillPaymentMainState,
    action: Action<IBillPaymentRemoveFavoriteRequest>,
  ) => addToFavorite(state, action.payload),
  [REMOVE_FAVORITE_AND_RECENTLY_ITEM]: (
    state: IBillPaymentMainState,
    action: Action<IBillPaymentAddFavoriteRequest>,
  ) => removeFromFavoriteAndRecently(state, action.payload),
  [TOGGLE_FOLD_FAVORITE]: (state: IBillPaymentMainState) => ({
    ...state,
    favoriteFold: !state.favoriteFold,
  }),
  [TOGGLE_FOLD_RECENTLY]: (state: IBillPaymentMainState) => ({
    ...state,
    recentlyFold: !state.recentlyFold,
  }),
  [SET_VIEW]: (state: IBillPaymentMainState, action: Action<any>) => ({
    ...state,
    view: {
      ...state.view,
      ...action.payload,
    },
  }),
  [SET_VERSION_INFO]: (state: IBillPaymentMainState, action: Action<any>) => ({
    ...state,
    version: action.payload,
  }),
  [SET_STARTTYPE]: (state: IBillPaymentMainState, action: Action<any>) => ({
    ...state,
    mainStarttype: action.payload,
  }),
  [CLEAR_STARTTYPE]: (state: IBillPaymentMainState) => ({
    ...state,
    mainStarttype: '',
  }),
}

export const billPaymentMainSelector = {
  view: (state: IRootState): IBillPaymentMainView =>
    state[BILL_PAYMENT_MAIN].view ?? initialState.view,
  historyRecents: (state: IRootState) =>
    state[BILL_PAYMENT_MAIN].recentlyList ?? initialState.recentlyList,
  historyBookmarks: (state: IRootState) =>
    state[BILL_PAYMENT_MAIN].favoriteList ?? initialState.favoriteList,
  paymentTypeList: (state: IRootState) =>
    state[BILL_PAYMENT_MAIN].paymentTypeList ?? initialState.paymentTypeList,
  productGroupTypeList: (state: IRootState, paymentTypeCode: string): IProductGroupType[] =>
    billPaymentMainSelector.paymentTypeList(state).find(item => item.code === paymentTypeCode)
      ?.productGroupList ?? EmptyPaymentType.productGroupList,

  productGroupType: (
    state: IRootState,
    paymentTypeCode: string,
    productGroupTypeCode: string,
  ): IProductGroupType =>
    billPaymentMainSelector
      .productGroupTypeList(state, paymentTypeCode)
      .find(group => group.code === productGroupTypeCode) ?? EmptyProductGroupType,
  productTypeList: (
    state: IRootState,
    paymentTypeCode: string,
    productGroupTypeCode: string,
  ): IProductType[] =>
    billPaymentMainSelector
      .productGroupTypeList(state, paymentTypeCode)
      .find(item => item.code === productGroupTypeCode)?.productTypeList ??
    EmptyProductGroupType.productTypeList,
  flattenProductType: (state: IRootState, productTypeCode: string): IFlattenProductType => {
    return state[BILL_PAYMENT_MAIN].flattenProductTypeMap[productTypeCode] ?? EmptyFlattenProduct
  },
  getversioninfo: (state: IRootState): any => {
    return state[BILL_PAYMENT_MAIN].version ?? null
  },
  getStarttype: (state: IRootState): string => {
    return state[BILL_PAYMENT_MAIN].mainStarttype
  },
}

export const billPaymentMainReducer = handleActions(reducer, initialState)

export function* billPaymentAddFavorite(action: Action<IBillPaymentAddFavoriteRequest>) {
  yield put(loadingActions.start(BILL_PAYMENT_MAIN))
  try {
    yield call(getCSRF)

    yield call(addFavoritePayment, action.payload)
    yield put(billMainAddFavoriteAsync.success(action.payload))
  } catch (e) {
    const errorObj: any = e
    switch (errorObj.code) {
      case STATUS_CODE.MAXIMUM_FAVORITE_COUNT_OVER:
        yield call(Bridge.showNotification, {
          message: $('billpayment_main_favorite_exceederror_toast'),
        })
        break

      default:
        yield put(errorActions.set(errorObj))
        break
    }
  }
  yield put(loadingActions.finish(BILL_PAYMENT_MAIN))
}

export function* billPaymentRemoveFavorite(action: Action<IBillPaymentRemoveFavoriteRequest>) {
  yield put(loadingActions.start(BILL_PAYMENT_MAIN))
  try {
    yield call(getCSRF)

    yield call(removeFavoritePayment, action.payload)
    yield put(billMainRemoveFavoriteAsync.success(action.payload))
  } catch (e) {
    const errorObj: any = e
    yield put(errorActions.set(errorObj))
  }
  yield put(loadingActions.finish(BILL_PAYMENT_MAIN))
}

export function* setPayStateByRecentlyAndFavoriteMenu(
  action: Action<IBillPaymentRecentAndFavorite>,
) {
  yield put(loadingActions.start(BILL_PAYMENT_MAIN))

  try {
    const { customerId, productTypeCode, productTypeSwitchId } = action.payload

    const {
      productTypeVoucherList,
      productTypeIsAutoFilled,
      productTypeIdentifierType,
    }: IFlattenProductType = yield select(
      billPaymentMainSelector.flattenProductType,
      productTypeCode,
    )

    // 해당 아이템의 정합성 체크
    // 금액을 미리 알 수 없는 선불금액의 경우(non auto-filled), 최소충전금액을 입력하여 validation 을 체크하도록 함
    // https://line-enterprise.slack.com/archives/CQ7LV0HBR/p1585206321027100?thread_ts=1585122752.013200&cid=CQ7LV0HBR
    const inquireRequestPayload: IBillPaymentInquireRequest = {
      amount: productTypeVoucherList[0],
      customerId: removeAllHypens(customerId),
      productTypeCode,
      productTypeSwitchId,
    }

    const response: IBillPaymentInquireResponse = yield call(fetchBillBeta, inquireRequestPayload)

    yield put(validateBillAsync.success(response))

    const formattedCustomerId =
      productTypeIdentifierType === BILLS_IDENTIFIER_TYPE_CODE.CREDITCARD
        ? getFormattedCardNo(response.customerId)
        : response.customerId

    yield put(billPaymentAmountActions.setCustomerId(formattedCustomerId))
    yield put(billPaymentAmountActions.setCustomerName(response.customerName ?? ''))

    if (productTypeIsAutoFilled) {
      yield put(billPaymentAmountActions.setAmount(response.amount ?? '0'))
      yield put(billPaymentAmountActions.setFee(response.fee ?? '0'))
      yield put(billPaymentAmountActions.setReserved(response.reserved))
    }

    yield put(
      billPaymentAmountActions.setTicketInfo({
        ticketInfo1: response.ticketInfo1 ?? '',
        ticketInfo2: response.ticketInfo2 ?? '',
        ticketInfo3: response.ticketInfo3 ?? '',
        ticketInfo4: response.ticketInfo4 ?? '',
        merchantName: response.merchantName ?? '',
      }),
    )

    // 다음 화면으로 이동
    yield call(push, `${BILL_PAYMENT_ROUTES.AMOUNT}/${productTypeCode}`)
  } catch (e) {
    const errorObj: any = e
    if (
      errorObj.code === STATUS_CODE.BILL_PAYMENT_TRANSACTION_FAIL ||
      errorObj.code === STATUS_CODE.BILL_PAYMENT_TRANSACTION_TOO_MANY_UNPAID_BILLS ||
      errorObj.code === STATUS_CODE.BILL_PAYMENT_TRANSACTION_ALREADY_PAID ||
      errorObj.code === STATUS_CODE.BILL_PAYMENT_LIMIT_FOR_ONE_DAY
    ) {
      const { customerId, productTypeName, trxPayKey } = action.payload
      yield put(
        billMainActions.setView({
          invalidProductTypeName: productTypeName,
          invalidCustomerId: customerId,
        }),
      )
      yield put(layerActions.open(LEAVE_ALERT_TYPE.INVALID_BILL_PAYMENT_INFO))

      yield put(billMainActions.removeFavoriteAndRecentlyItem(action.payload))
      yield call(removeRecentPayment, { trxPayKey })
    } else {
      yield put(errorActions.set(errorObj))
    }
  } finally {
    yield put(loadingActions.finish(BILL_PAYMENT_MAIN))
  }
}

export function* resetPayStateByRecentlyAndFavoriteMenu() {
  // 경고창 닫기
  yield put(layerActions.close(LEAVE_ALERT_TYPE.INVALID_BILL_PAYMENT_INFO))
  yield put(
    billMainActions.setView({
      invalidProductTypeName: '',
      invalidCustomerId: '',
    }),
  )
}

export function* toggleFoldFavorite() {
  const { favoriteFold }: IBillPaymentMainState = yield select(state => state[BILL_PAYMENT_MAIN])
  storeFoldSetting(BILL_PAYMENT_FOLD_LOCAL_STORAGE_KEY, Category.FAVORITE, favoriteFold)
}
export function* toggleFoldRecently() {
  const { recentlyFold }: IBillPaymentMainState = yield select(state => state[BILL_PAYMENT_MAIN])
  storeFoldSetting(BILL_PAYMENT_FOLD_LOCAL_STORAGE_KEY, Category.RECENTLY, recentlyFold)
}

export function* billPaymentMain() {
  try {
    yield put(loadingActions.start(BILL_PAYMENT_MAIN))
    const response: IBillPaymentMainResponse = yield call(fetchBillPaymentMainBeta)
    console.warn(response.minDisbursalAmount)
    yield put(billMainAsync.success(response))
  } catch (e) {
    const errorObj: any = e
    if (errorObj.code === STATUS_CODE.BILL_PAYMENT_TRANSACTION_FAIL) {
      yield put(layerActions.open(LEAVE_ALERT_TYPE.BILL_PAYMENT_TRANSACTION_FAIL))
    } else {
      yield put(errorActions.set(errorObj))
    }
  } finally {
    yield put(loadingActions.finish(BILL_PAYMENT_MAIN))
  }

  if (deeplinkFn.get() !== null) {
    yield call(push, `${BILL_PAYMENT_ROUTES.PRODUCT_GROUP}/${deeplinkFn.get()}`)
    yield deeplinkFn.set(null)
  }
}

export const billPaymentMainSaga = [
  // takeLatest(billMainAsync.FETCH, createSaga(billMainAsync, fetchBillPaymentMainBeta)),
  takeLatest(billMainAsync.FETCH, billPaymentMain),
  takeLatest(billMainAddFavoriteAsync.POST, billPaymentAddFavorite),
  takeLatest(billMainRemoveFavoriteAsync.POST, billPaymentRemoveFavorite),
  takeLatest(SET_PAY_STATE_BY_RECENTLY_AND_FAVORITE_MENU, setPayStateByRecentlyAndFavoriteMenu),
  takeLatest(RESET_PAY_STATE_BY_RECENTLY_AND_FAVORITE_MENU, resetPayStateByRecentlyAndFavoriteMenu),
  takeLatest(TOGGLE_FOLD_FAVORITE, toggleFoldFavorite),
  takeLatest(TOGGLE_FOLD_RECENTLY, toggleFoldRecently),
]
